import { useEffect } from 'react';

const NON_TEXT_INPUT_TYPES = new Set([
	'button',
	'checkbox',
	'color',
	'file',
	'hidden',
	'image',
	'radio',
	'range',
	'reset',
	'submit',
]);

/** True for an element that opens the on-screen keyboard when focused. */
export const isTextField = (element: Element | null): boolean => {
	if (element instanceof HTMLTextAreaElement) return true;
	if (element instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(element.type);
	return element instanceof HTMLElement && element.isContentEditable;
};

// Space left between the top of the visible area and the drawer while typing.
const TOP_GAP_PX = 24;

/**
 * Keeps a bottom drawer above the on-screen keyboard while one of its text fields
 * has focus.
 *
 * vaul's own `repositionInputs` lifts the drawer by the keyboard height but ignores
 * `visualViewport.offsetTop`: when iOS (Safari or the app's WKWebView) also scrolls
 * the page to reveal the field, the drawer is lifted twice and leaves the screen
 * through the top, and whether iOS scrolls depends on timing. Here the drawer's
 * bottom edge is pinned to the bottom of the visible area (offset included) and its
 * height is capped to that area, so its content scrolls inside it. Use it with
 * `repositionInputs={false}` on the vaul root. It takes the element (not a ref) so
 * it starts when the drawer's content actually mounts, which is after the drawer opens.
 */
export const useDrawerKeyboardInset = (drawer: HTMLElement | null) => {
	useEffect(() => {
		const viewport = window.visualViewport;
		if (!viewport || !drawer) return;

		let applied = false;
		let frame = 0;

		const reset = () => {
			if (!applied) return;
			drawer.style.bottom = '';
			drawer.style.maxHeight = '';
			applied = false;
		};

		const update = (revealField: boolean) => {
			const focused = document.activeElement;
			const typing = isTextField(focused) && drawer.contains(focused);
			// Layout pixels below the visible area: the keyboard, wherever iOS scrolled to.
			const covered = window.innerHeight - viewport.height - viewport.offsetTop;
			if (!typing || covered < 1) {
				reset();
				return;
			}
			drawer.style.bottom = `${Math.round(covered)}px`;
			drawer.style.maxHeight = `${Math.round(viewport.height - TOP_GAP_PX)}px`;
			applied = true;
			if (revealField) focused?.scrollIntoView({ block: 'nearest' });
		};

		// Focus moves land before the keyboard settles: measure on the next frame.
		const scheduleUpdate = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => update(true));
		};
		const onResize = () => update(true);
		const onScroll = () => update(false);

		viewport.addEventListener('resize', onResize);
		viewport.addEventListener('scroll', onScroll);
		document.addEventListener('focusin', scheduleUpdate);
		document.addEventListener('focusout', scheduleUpdate);
		return () => {
			cancelAnimationFrame(frame);
			viewport.removeEventListener('resize', onResize);
			viewport.removeEventListener('scroll', onScroll);
			document.removeEventListener('focusin', scheduleUpdate);
			document.removeEventListener('focusout', scheduleUpdate);
			reset();
		};
	}, [drawer]);
};
